import { floor, inRange, isFunction, lowerFirst, memoize, throttle } from "lodash-es";
import SparkMD5 from "spark-md5";
import utils from "./utils";
import Vue from "vue";
import { getToken } from "../index.js";
import docService from "@/CommonDP/service/doc-service";
import { Message } from "element-ui";
let id = 0;
const STATUS = utils.STATUS;
const StatusMap = utils.StatusMap;
const queryHidden = memoize((prjId) => {
  const pro = prjId ? docService.queryhiddenFolder(prjId) : docService.queryEnterpriseIdhiddenFolder(store.state.enterprise.enterpriseId);
  pro.catch(() => queryHidden.cache.delete(prjId));
  return pro.then((res) => res.data);
});
/**
 * @typedef Task
 * @type {Function & {retries:number,file:IFile,weight:number} }
 */
/**
 * @typedef IParams
 * @property {string}  folderId			所在目录id，它与 filename是一组，fileId是另一组，两组不能全部为空
 * @property {string}  filename			文件名（带后缀）
 * @property {string}  enterpriseId	企业id
 * @property {number?}  currentVersion		当前文件的版本号，空或0时，此参数无效。如果不为空，则在上传时，服务端还要判断文件的当前版本是不是等于它，如果不是，则仍拒绝上传。当fileId为空时，此参数无效。
 * @property {string?}  relativePath		相对路径。如果在当前目录下，则其值为空或文件名。如果在子目录下，则其值为：子目录1/子目录2/文件名。当fileId不为空时，此参数无效。
 * @property {number?}  overlapStrategy		同名文件覆盖策略，空或0默认策略（非隐藏目录升版、隐藏目录创建新文件），1-创建新文件，2-强制升版。当fileId非空时，此参数无效。
 * @property {number?}  specialFileType		特殊的文件类型，取值参考后台提供的枚举表。一般保持空。如果值是空或0或99，后台将自动计算文件类型；否则后台会参考兼容的类型，以决定是否取这个值。比如：当文件名是xxx.dwg, 但指定类型为4(PDF)，则最终类似仍不会为4，因为不是兼容的类型。当fileId非空时，此参数无效。
 * @property {number?}  isIncrementFile	是否为增量文件，空或0-否（表示为全量文件），1-是
 * @property {number?}  clientType	客户端类型，空或0-web端，1-插件端，默认0
 * @property {number?}  isImportant	是否重要版本，空或0-否，1-是，默认0
 * @property {number?}  isCheckOut	是否锁定此文件，空或0-否，1-是，默认0
 * @property {number?}  isLight	是否在服务端自动推送轻量化，0-否，1-是（自动），空或2-由于其它参数控制（包括客户端类型，服务端参数配置等），默认空.对于不能轻量化的文件，就算值为1也不会推送。
 * @property {string?}  remark	备注
 * @property {string?}  prjId	关联的项目id
 * @property {string?}  flowInstanceId	关联的流程id，如果有值，则通过它与用户是否关联来进行权限校验，可跳过项目内成员的权限校验
 * @property {Blob}  file	文件内容（二进制）
 */
class Pools {
  /** @type {IUploader} */
  uploader;
  weakMap = new WeakMap()
  /** @type {Task[]}  */
  list = [];
  pending = new Set();
  constructor(uploader) {
    this.uploader = uploader;
  }
  /**
   * @param {Task} task
   * @param {boolean?} start
   * @returns {Promise} 该方法执行后的返回数据
   */
  add(task) {
    if (!isFunction(task)) return Promise.reject();
    const list = this.list;
    if (list.indexOf(task) + 1) return this.weakMap.get(task);
    const { weight = 1 } = task;
    const index = list.findIndex(t => t.weight < weight);
    if (index === -1) list.push(task);
    else list.splice(index, 0, task);
    // 返回一个promise,将在任务完成后处理
    const resolvers = utils.withResolvers();
    this.weakMap.set(task, resolvers);
    this.run();
    return resolvers.promise;
  }
  /** @param {Task} task  */
  async getRetriesFn(task) {
    let value;
    let loop = false;
    do {
      try {
        value = await task();
        loop = false;
      } catch (error) {
        if (task.retries) {
          loop = true;
          task.retries--;
          task.file.emit('retry', error, task.file, task.chunk);
        } else throw error;
      }
    } while (loop);
    return Promise.resolve(value);
  }
  run() {
    const { list, pending, uploader, weakMap } = this;
    const max = uploader.opts.simultaneous;
    if (pending.size >= max) return;
    if (!list.length) return;
    const fn = list.shift();
    const pro = this.getRetriesFn(fn);
    pending.add(pro);
    pro.finally(() => {
      this.pending.delete(pro);
      this.run();
    });
    // 处理添加任务时的promise
    const handler = weakMap.get(fn);
    if (handler) pro.then(handler.resolve, handler.reject);
  }
  /** @param {Task} task  */
  remove(task) {
    if (!isFunction(task)) return;
    const index = this.list.indexOf(task);
    task.retries = 0;
    if (index + 1) this.list.splice(index, 1);
    const handler = this.weakMap.get(task);
    handler && handler.reject();
  }
  /** @param {IFile?} file */
  clear(file) {
    this.list.filter((task) => !file || task.file === file).forEach(task => this.remove(task));
  }
}
const defaults = {
  maxSize: 5368709120, // 5G
  chunkSize: 8388608, // 8M
  chunkThreshold: 52428800, // 大于50M才允许分片上传
  autoStart: true,
  accept: '*',
  multiple: false,
  isDirectory: false,
  target: 'doc/chunk',
  once: false,
  maxChunkRetries: 2,
  simultaneous: 4,
  fileParameterName: 'file', // 最大上传数量
  headers: {
    get Authorization() {
      return getToken();
    }
  },
  /** @type {IParams} */
  params: {},
  /** @type {(string|(this:IFile)=>any)[]}  */
  steps: ['getIdentifier', "waitLock", 'test', 'chunk', 'merge'],
  /** @type {{[methodName:string]:Function} }   */
  methods: {},
  /** @type {(file:IFile)=>Promise<string>|string} */
  generateUniqueIdentifier: async (file) => {
    const spark = new SparkMD5.ArrayBuffer();
    const chunks = file.chunks;
    for (const chunk of chunks) {
      const chuckBlob = chunk.raws;
      const buffer = await chuckBlob.arrayBuffer();
      spark.append(buffer);
    }
    const md5str = spark.end();
    spark.destroy();
    return md5str;
  },
  /** @type {import("vue").AsyncComponent} */
  panel: null,
  showPanel: true
};
export class IUploader {
  static utils = utils
  static STATUS = utils.STATUS
  static defaults = defaults;
  pools = new Pools(this);
  /** @type {IUploader} */
  uploader;
  /** @type {IUploader.defaults} */
  opts = {};
  /** @type {IFile[]} */
  files = [];
  /** @param {IUploader.defaults} opts */
  constructor(opts = {}) {
    Object.assign(this.opts, IUploader.defaults, opts);
    this.uploader = this;
    this.createPanel();
  }
  /**
   * @description 添加文件
   * @param {File|File[]|FileList} file
   * @param {IUploader.defaults} opts
   * @returns {{list:IFile[],promise:Promise<Vo[]>}}
   */
  addFile(file, opts) {
    const _files = [];
    if (file.length) _files.push(...file);
    else _files.push(file);
    const list = [];
    for (const raw of _files) {
      const file = new IFile(raw, this, opts);
      if (file.ignore) {
        file.remove();
      } else {
        list.push(file);
        file.opts.autoStart && file.start();
      }
    }
    const first = list[0];
    /** @type {Promise<[]>} */
    let promise;
    if (first) {
      if (first.opts.showPanel) this.showPanel(true);
      first.emit('listAdd', list);
      this.files.push(...list);
      promise = Promise.all(list.map(f => f.promise));
      promise.then(res => {
        first.emit('complete', res, list);
        if (first.opts.once) {
          for (const file of list) file.remove();
        }
      });
    } else {
      promise = Promise.resolve([]);
    }
    return { list, promise };
  }
  /** @type {import("vue").ComponentInstance}  */
  panel
  /**
   * 如果有配置面板组件,打开/创建上传面板
   */
  async createPanel(showPanel = this.opts.showPanel) {
    if (!showPanel || this.panel) return;
    const asyncComponent = this.opts.panel;
    if (asyncComponent) {
      const el = document.createElement('div');
      document.body.appendChild(el);
      const exportValue = await asyncComponent();
      const options = exportValue.default || exportValue;
      const uploader = this;
      const data = { uploader, visible: true };
      const Panel = Vue.extend(options);
      const vm = new Panel({ el, data() { return data; } });
      this.panel = vm;
    }
  }
  showPanel(show) {
    if (this.panel) {
      this.panel.visible = !!show;
    } else if (show) {
      this.createPanel(true);
    }
  }
  clear() {
    const { files } = this;
    while (files.length) {
      files[0].remove();
    }
  }
  /**
   * @param {IUploader.defaults} opt
   * @returns {Promise<Vo[]>}
   **/
  async open(opt) {
    const opts = Object.assign({}, this.opts, opt);
    const fileList = await getInputFile(opts);
    return this.addFile(fileList, opts).promise;
  }
  async getHiddenParams(params = {}) {
    if (!params.prjId) params.prjId = "EnterpriseDoc";
    if (!params.folderId) params.folderId = await queryHidden(params.prjId);
    return params;
  }
  destroy() {
    this.clear();
    this.pools.clear();
    const panel = this.panel;
    panel && panel.$destroy();
    this.panel = null;
  }
  directive() {
    const uploader = this;
    /** @type {{[k:string]:(e:DragEvent)=>void } } */
    let fns = null;
    /** @type {import("vue").ObjectDirective<Element>} */
    return {
      bind(el, bind) {
        fns = {};
        let mask;
        fns.dragenter = (evt) => {
          evt.preventDefault();
          if (mask) return;
          mask = document.createElement('div');
          /** @type {CSSStyleDeclaration} */
          const style = {
            height: "100vh",
            width: "100vw",
            position: "fixed",
            inset: "0",
            display: "flex",
            flexFlow: "row",
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: "rgba(71, 119, 231, 0.08)",
            backdropFilter: "blur(1px) grayscale(40%)",
            fontSize: "24px",
            fontWeight: 'bold',
            color: "#999",
            border: '2px solid #4777E7',
            zIndex: utils.nextZIndex()
          };
          const label = document.createElement('div');
          const span1 = document.createElement('span');
          const span2 = document.createElement('span');
          Object.assign(label.style, {
            background: '#F7F9FC',
            padding: "5px 14px",
            border: '1px solid #4777E7',
            pointerEvents: 'none'
          });
          span1.innerText = "上传到";
          span2.innerText = "当前文件夹下";
          span1.style.color = "#4777E7";
          label.appendChild(span1);
          label.appendChild(span2);
          Object.assign(mask.style, style);
          mask.classList.add('mask');
          mask.appendChild(label);
          mask.ondragleave = fns.dragleave;
          mask.ondrop = fns.drop;
          mask.ondragover = fns.dragover;
          evt.dataTransfer.dropEffect = "move";
          el.appendChild(mask);
        };
        fns.dragleave = (evt) => {
          evt.preventDefault();
          if (mask) el.removeChild(mask);
          mask = null;
        };
        fns.dragover = (evt) => {
          // 阻止默认行为以允许放置
          evt.preventDefault();
        };
        fns.drop = (evt) => {
          evt.stopPropagation();
          evt.preventDefault();
          fns.dragleave(evt);
          const cb = bind.value;

          if (isFunction(cb)) {
            readDataTransfer(evt.dataTransfer)
              .then((files) => {
                cb(
                  files,
                  (files, opts) => uploader.addFile(files, opts).promise,
                  uploader
                );
              }
              );
          }
        };
        document.addEventListener('dragenter', fns.dragenter);
      },
      unbind() {
        document.removeEventListener('dragenter', fns.dragenter);
        fns = null;
      }
    };
    /**
     * 解析拖拽数据
     * @param {DataTransfer} dataTransfer
     */
    async function readDataTransfer(dataTransfer) {
      const list = [];
      /** @type {DataTransferItem[]} */
      const loops = [];
      if (dataTransfer.items && dataTransfer.items.length) {
        loops.push(...[...dataTransfer.items].map(item => item.webkitGetAsEntry()));
        for (const entry of loops) {
          if (!entry) continue;
          if (entry.isFile) {
            const file = await readFile(entry);
            list.push(file);
          } else if (entry.isDirectory) {
            const reader = entry.createReader();
            let entries;
            do {
              entries = await readDirectory(reader);
              loops.push(...entries);
            } while (entries && entries.length);
          }
        }
      } else if (dataTransfer.files && dataTransfer.files[0]) {
        list.push(...dataTransfer.files);
      }
      return list;
    }
    /** @param {FileEntry} entry */
    function readFile(entry) {
      return new Promise((resolve, reject) => {
        entry.file((file) => {
          file.relativePath = entry.fullPath.substring(1);
          resolve(file);
        }, reject);
      });
    }
    /** @param {FileSystemDirectoryReader} reader  */
    function readDirectory(reader) {
      return new Promise((resolve, reject) => {
        reader.readEntries(entries => {
          resolve([...entries]);
        }, reject);
      });
    }
  }
}
function getInputFile(opts) {
  return new Promise((resolve) => {
    const inputEl = document.createElement('input');
    inputEl.type = 'file';
    inputEl.hidden = true;
    inputEl.accept = opts.accept;
    inputEl.multiple = opts.multiple;
    if (opts.isDirectory) {
      inputEl.webkitdirectory = true;
    }
    inputEl.addEventListener('change', event => {
      if (event.target.value) {
        const list = [...event.target.files];
        resolve(list);
        event.target.value = '';
      }
    }, { once: true });
    inputEl.click();
    inputEl.remove();
  }
  );
}
class IFile {
  id = id++;
  /** @type {IUploader} */
  name = ''
  fullName = '';
  uploader;
  status = STATUS.PAUSE;
  statusText = '';
  statusMap = {}
  /** @type {IUploader.defaults } */
  opts = {}
  message = "";
  identifier = '';
  /** @type {[key:string]:any} */
  dataset = {};
  /**
   * @param {File} file
   * @param {IUploader} uploader
   * @param {IUploader.defaults?} opts 文件配置项 同uploader
   */
  constructor(file, uploader, opts) {
    this.uploader = uploader;
    Object.assign(this.opts, this.uploader.opts, opts);
    Object.assign(this.statusMap, StatusMap, this.opts.statusMap);
    this.raws = file;
    this.name = file.fileName || file.name;
    this.fullName = file.relativePath || file.webkitRelativePath || this.name;
    this.size = file.size;
    this.type = file.type;
    this._optEvent();
    this.validate();
    if (this.ignore) return;
    const { promise, resolve } = utils.withResolvers();
    this.#resolve = resolve;
    this.promise = promise;
    this.buildChunks();
  }
  get nextChunk() {
    const chunks = [...this.chunks];
    if (chunks.length > 2) chunks.splice(1, 0, chunks.pop());
    return chunks.find((chunk) => !chunk.promise && chunk.status === STATUS.PENDING);
  }
  get headers() {
    return { ...this.uploader.opts.headers, ...this.opts.headers };
  }
  get params() {
    return { ...this.uploader.opts.params, ...this.opts.params };
  }
  ignore = true;
  validate() {
    const range = inRange(this.size, 1, this.opts.maxSize);
    const add = this.emit('add', this);
    this.ignore = !(range && add);
  }
  get isStopped() {
    return [STATUS.ERROR, STATUS.PAUSE, STATUS.SUCCESS].indexOf(this.status) + 1;
  }
  _status(status) {
    this.status = status;
    this.statusText = StatusMap[status];
  }
  _optEvent() {
    Object.keys(this.opts).forEach((key) => {
      if (/^on[A-Z]\S+/.test(key)) {
        const item = this.opts[key];
        if (isFunction(item)) {
          const eventName = lowerFirst(key.substring(2));
          this.on(eventName, item);
        }
      }
    });
  }
  #last_size = 0;
  #last_size_time = 0;
  speed = 0;
  progress = 0
  setProgress = throttle(this._setProgress, 800);
  _setProgress() {
    const lastTime = this.#last_size_time;
    const lastSize = this.#last_size;
    const curTime = Date.now();
    let curSize = 0;
    let totalSize = 0;
    for (const chunk of this.chunks) {
      curSize += chunk._lastSize;
      totalSize += chunk._total || chunk.size;
    }
    this.#last_size_time = curTime;
    this.#last_size = curSize;
    const size = Math.max(curSize - lastSize, 0);
    const time = Math.max(curTime - lastTime, 0);
    const is_upload = time && size;
    this.progress = is_upload ? floor(curSize / totalSize * 100, 1) : 0;
    const speed = is_upload ? ~~((size / time) * 1000) : 0;
    this.speed = (this.speed + speed) / 2;
  }
  /** @description 文件块数 */
  total = 0;
  /** @type {IChunk[]} */
  chunks = [];
  get isChunkedUpload() { return this.total > 1; }
  /**
   * 构建文件的分块
   * 该方法根据文件大小和指定的块大小计算文件的总分块数，创建对应的分块实例并存储在当前实例的`chunks`属性中
   **/
  buildChunks() {
    const chunkSize = this.opts.chunkSize;
    const fileSize = this.size;
    const total = ~~(fileSize / chunkSize) || 1;
    const chunkThreshold = this.opts.chunkThreshold;
    const isSmallFile = chunkThreshold && fileSize <= chunkThreshold;
    if (total === 1 || isSmallFile) {
      this.total = 1;
      const chunk = new IChunk(this, 0);
      chunk.endByte = fileSize;
      chunk.size = fileSize;
      this.chunks = [chunk];
    } else {
      this.total = total;
      const chunks = [];
      for (let i = 0; i < total; i++) chunks.push(new IChunk(this, i));
      this.chunks = chunks;
    }
  }
  /** @description 取消请求控制器 */
  /** @type {AbortController} */
  _abort_handler
  /**
   * @type {Promise<Vo>}
   * @description 文件上传promise,允许失败
   **/
  #promise;
  /**
   * @type {Promise<Vo>}
   * @description 只有成功才会resolve
   **/
  promise
  #resolve
  /**
   * @param {Task} task
   * @param {Task} opt
   * @returns {Promise}
   */
  task(task, opt) {
    if (isFunction(task)) {
      task.retries ??= 0;
      task.file ??= this;
      task.weight ??= 1;
      Object.assign(task, opt);
      const pro = this.uploader.pools.add(task);
      return pro;
    }
  }
  send() {
    if (!this.#promise) {
      this.#promise = this._send();
      this.#promise.then((data) => {
        this._status(STATUS.SUCCESS);
        const vo = new ResponseFile(data);
        this.emit('success', vo.data, this);
        this.#resolve(vo.data);
      }, (thrown) => {
        if (this.isStopped) return;
        this.emit('error', thrown, this);
        this._status(STATUS.ERROR);
        const msg = utils.message(thrown);
        msg && Message.error(msg);
      });
    }
    return this.#promise;
  }
  /** @returns {Promise<Vo>} */
  async _send() {
    this.#last_size = 0;
    this.#last_size_time = 0;
    this.speed = 0;
    this._abort_handler = new AbortController();
    if (this.isChunkedUpload) {
      let lastReturnValue = this;
      for (const methodName of this.opts.steps) {
        const fn = isFunction(methodName) ? methodName : this.opts.methods[methodName] || this[methodName];
        if (isFunction(fn)) {
          if (this.destroyed) return Promise.reject('文件已取消');
          lastReturnValue = await Reflect.apply(fn, this, [lastReturnValue, this]);
        }
      }
      return lastReturnValue;
    } else {
      for (const chunk of this.chunks) {
        chunk.promise = null;
        chunk.status = STATUS.PENDING;
        chunk._lastSize = 0;
      }
      const [{ data }] = await this.chunk();
      return data;
    }
  }
  async waitLock() {
    if (this.total === 1) return;
    return this.task(async () => {
      let lock = true;
      do {
        if (this.destroyed) throw new Error("上传已取消");
        // 检测md5锁,md5锁会继续循环
        const promise = IUploader.utils.axios({
          headers: this.headers,
          url: "/doc/lockForUpload",
          signal: this._abort_handler.signal,
          params: {
            identifier: this.identifier,
            fileName: encodeURIComponent(this.fullName)
          }
        });
        promise.catch((thrown) => {
          const msg = utils.message(thrown) || "文件检测失败";
          this.abort(STATUS.ERROR, msg);
        });
        const { data } = await promise;
        lock = data === 1;
        // 成功,将跳出循环
        await utils.sleep(3000);
      } while (lock);
    }, { weight: -1, retries: 2 });
  }
  async chunk() {
    let chunk = this.nextChunk;
    this._status(STATUS.PENDING);
    const req = [];
    while (chunk) {
      const pro = chunk.send();
      req.push(pro);
      chunk = this.nextChunk;
    }
    const data = await Promise.all(req);
    this.progress = 100;
    return data;
  }
  async getIdentifier() {
    if (!this.identifier) {
      this._status(STATUS.TEST);
      this.identifier = await this.opts.generateUniqueIdentifier(this);
      this._status(STATUS.PENDING);
    }
    return this.identifier;
  }
  test() {
    this._status(STATUS.TEST);
    const promise = this.task(() => {
      return utils.axios({
        url: 'doc/checkChunk',
        method: 'POST',
        data: {
          chunkSize: this.opts.chunkSize,
          totalSize: this.size,
          identifier: this.identifier,
          filename: this.name,
          relativePath: this.fullName,
          totalChunks: this.total,
          ...this.params
        },
        headers: this.headers,
        signal: this._abort_handler.signal
      });
    }, { retries: 3 });
    promise.then(({ data }) => {
      const { skipUpload, uploaded } = data;
      this.dataset.test = promise;
      this.chunks.forEach((chunk) => {
        const loaded = (!!skipUpload) || !!(uploaded.indexOf(chunk.offset) + 1);
        chunk.promise = null;
        if (loaded) {
          chunk.status = STATUS.SUCCESS;
          chunk._lastSize = chunk.size;
        } else {
          chunk.status = STATUS.PENDING;
          chunk._lastSize = 0;
        }
      });
    }, (thrown) => {
      const msg = utils.message(thrown) || '校验文件失败';
      this.abort(STATUS.ERROR, msg);
    });
    return promise;
  }
  /** 合并文件并查询 */
  merge() {
    this._status(STATUS.MERGE);
    const p = this._merge();
    p.catch((thrown) => {
      const msg = utils.message(thrown) || '合并文件失败';
      this.abort(STATUS.ERROR, msg);
    });
    return p;
  }
  async _merge() {
    const reqList = this.chunks.map(t => t.promise).filter((t) => t);
    await Promise.all(reqList);
    const { data: { skipUpload, needMerge, fileInfo }} = await this.dataset.test;
    // 只有 skipUpload && !needMerge 才会跳过合并
    if (!needMerge && skipUpload) {
      return fileInfo;
    } else {
      return this.task(() => {
        return utils.axios({
          method: 'post',
          url: '/doc/noPermission/mergeFile/fullAmountAsync',
          signal: this._abort_handler.signal,
          headers: this.headers,
          data: {
            ...this.params,
            filename: this.name,
            identifier: this.identifier,
            relativePath: this.fullName
          }
        });
      }, { weight: 2, retries: 2 })
        .then(({ data = {}}) => {
          const { asyncKey } = data;
          if (asyncKey) {
            return this._waitMerge(asyncKey, 1);
          } else {
            return data;
          }
        });
    }
  }
  // 循环调用当前文件合并状态
  async _waitMerge(asyncKey, retries = 0) {
    do {
      await utils.sleep(5000);
      if (this.destroyed) throw new Error('文件已取消');
      const { data: fileInfo } = await utils.axios({
        url: '/doc/mergeFile/queryMergeResult',
        params: {
          asyncKey,
          prjId: this.params.prjId
        },
        signal: this._abort_handler.signal,
        headers: this.headers
      });
      if (!fileInfo) {
        if (retries < 1) throw new Error({ data: { message: "获取合并数据失败" }});
        retries--;
      } else if (!fileInfo.asyncKey && fileInfo.fileId) return fileInfo;
    } while (this.status === STATUS.MERGE);
  }
  start() {
    if ([STATUS.ERROR, STATUS.PAUSE].indexOf(this.status) + 1) {
      this._abort_handler = new AbortController();
      this.#promise = null;
      this._status(STATUS.PENDING);
      this.emit('start', this);
    }
    return this.send();
  }
  pause() {
    if (this.isStopped) return;
    this.emit('pause', this);
    this.abort(STATUS.PAUSE, '已暂停');
  }
  abort(status = STATUS.ERROR, message = "") {
    if (this.isStopped) return;
    this._abort_handler?.abort?.();
    this.uploader.pools.clear(this);
    this._status(status);
    if (message) this.message = message;
  }
  destroyed = false
  remove() {
    const list = this.uploader.files;
    const index = list.indexOf(this);
    list.splice(index, 1);
    this.pause();
    this.destroyed = true;
    this.emit('remove', this);
    this.chunks.splice(0, this.chunks.length);
    this.raws = null;
    this.off();
  }
  /** @type { Map<string,Set<Function>>} } */
  #event = new Map();
  on(name, func) {
    if (name && isFunction(func)) {
      const events = this.#event.get(name) || new Set();
      events.add(func);
      this.#event.set(name, events);
    }
  }
  off(name, func) {
    const map = this.#event;
    if (!name) map.clear();
    else if (!func) {
      const events = map.get(name);
      events && events.clear();
    } else {
      map.delete(func);
    }
  }
  /**
   * @param {*} name
   * @param  {...any} args 参数集
   * @returns {boolean} 返回false取消默认操作
   */
  emit(name, ...args) {
    if (name) {
      const map = this.#event;
      const fnSet = map.get(name);
      if (!fnSet) return true;
      try {
        return [...fnSet].every(fn => Reflect.apply(fn, this, args) !== false);
      } catch (error) {
        return false;
      }
    }
    return true;
  }
}
class IChunk {
  /** @type {IUploader} */
  uploader
  /** @type {IFile} 当前文件 */
  file;
  offset = 0;
  endByte = 0;
  startByte = 0;
  retries = 0;
  promise;
  status = STATUS.PENDING
  #raws;
  /** @type {Blob} */
  get raws() {
    if (this.#raws) return this.#raws;
    const raws = this.file.raws.slice(this.startByte, this.endByte);
    this.#raws = raws;
    return raws;
  }
  /**
   * @param {IFile} file
   * @param {Number} offset
   */
  constructor(file, offset) {
    const uploader = file.uploader;
    this.file = file;
    this.offset = offset + 1;
    this.uploader = uploader;
    this.retries = file.opts.maxChunkRetries;
    const chunkSize = file.opts.chunkSize;
    this.startByte = offset * chunkSize;
    this.endByte = this.offset < file.total ? this.startByte + chunkSize : file.size;
    this.size = this.endByte - this.startByte;
    this.chunkSize = chunkSize;
  }
  send() {
    if (!this.promise) {
      const promise = this._send();
      promise.then(() => {
        this.status = STATUS.SUCCESS;
      }, () => {
        this.status = STATUS.ERROR;
      });
      this.promise = promise;
    }
    return this.promise;
  }
  _lastSize = 0;
  _total = 0;
  _send() {
    this._lastSize = 0;
    this._total = 0;
    const { file } = this;
    const isChunked = file.isChunkedUpload;
    return file.task(() => {
      this.status = STATUS.UPLOADING;
      file._status(STATUS.UPLOADING);
      const url = isChunked ? file.opts.target : '/doc/uploadFile';
      const params = isChunked
        ? {
          chunkNumber: this.offset,
          currentChunkSize: this.size,
          chunkSize: file.opts.chunkSize,
          totalSize: file.size,
          identifier: file.identifier,
          filename: file.name,
          relativePath: file.fullName,
          totalChunks: file.total,
          clientType: 0,
          ...file.params
        }
        : {
          filename: file.name,
          relativePath: file.fullName,
          clientType: 0,
          ...file.params
        };
      const formData = new FormData();
      Object.keys(params).forEach(k => formData.set(k, params[k]));
      formData.append(file.opts.fileParameterName, this.raws);
      file.emit('upload', formData, this, file);
      const promise = utils.axios({
        url,
        method: 'post',
        data: formData,
        headers: file.headers,
        signal: file._abort_handler.signal,
        onUploadProgress: (event) => {
          if (event.lengthComputable) {
            this._lastSize = event.loaded;
            this._total = event.total;
            this.file.setProgress();
            this.file.emit('progress', event, this);
          }
        }
      });
      return promise;
    }, { chunk: this, retries: file.opts.maxChunkRetries, weight: 1.1 });
  }
}

/**
 * @typedef Vo
 * @property {Object} data
 * @property {string} data.fileId
 * @property {string} data.folderId
 * @property {string} data.fileName
 * @property {number} data.fileType
 * @property {string} data.versionIds
 * @property {string} data.prjId
 * @property {number} data.fileSize
 * @property {string} data.readableFileSize
 * @property {string} data.fileMd5
 * @property {string} data.checkInDate
 * @property {string} data.checkInUser
 * @property {string} data.userId
 **/
class ResponseFile {
  /** @type {Vo} */
  data;
  /** @param {Vo} vo - 包含文件相关数据的对象 */
  constructor(vo) {
    if (!vo) throw new Error('Error creating FileResponseData from empty data');
    vo.checkInUser = vo.checkInUser || vo.operUserName;
    vo.checkInDate = vo.checkInDate || vo.createTime;
    vo.versionIds = vo.versionIds || vo.versionId || vo.versionID || vo.fileVersion || vo.version;
    this.data = vo;
  }
}
